/**
 * The filters that compare a cell against a single typed value.
 *
 * Equality, ordering, substring and emptiness -- everything except the date
 * families and the array-cell families, which have their own modules.
 */
import type { Field } from '@/api/types'

import { behaviorOf } from '../fields/behavior'
import type { ContainsKind } from '../fields/behavior/behavior'
import {
  filenameContains,
  genericContains,
  genericContainsWord,
  type FileCellItem,
} from './predicates'
import {
  durationHandler,
  numberHandler,
  ratingHandler,
  textHandler,
  stringify,
  type FilterValueHandler,
} from './handlers'
import type { FilterContext, MatchResult } from './types'

/**
 * The handler a filter should read a given field through.
 *
 * A formula resolves to the handler for the type its result behaves like, which
 * is why this dispatches on the field rather than on its type name alone.
 */
export function handlerFor(field: Field): FilterValueHandler {
  switch (resolveHandlerType(field)) {
    case 'duration':
      return durationHandler
    case 'rating':
      return ratingHandler
    // An autonumber is a number for filtering purposes, so "= 10" matches the
    // tenth row rather than comparing the text "10".
    case 'number':
    case 'autonumber':
      return numberHandler
    default:
      return textHandler
  }
}

function resolveHandlerType(field: Field): string {
  if (field.type === 'formula' || field.type === 'rollup' || field.type === 'count') {
    const formulaType = (field as { formula_type?: string }).formula_type
    return formulaType ?? field.type
  }
  return field.type
}

/** Renders a cell as the text the grid shows, for the substring filters. */
function humanReadable(field: Field, value: unknown, context?: FilterContext): string {
  return behaviorOf(field.type).toHumanReadable(field, value, context)
}

function containsKindOf(field: Field): ContainsKind {
  return behaviorOf(field.type).containsKind(field)
}

function containsWordKindOf(field: Field): ContainsKind {
  return behaviorOf(field.type).containsWordKind(field)
}

/* --------------------------------------------------------------- equality */

/**
 * Whether the cell equals the filter value.
 *
 * An empty filter value abstains, so clearing the input shows every row -- but
 * only on a column whose handler produces the empty string for it. On a numeric
 * column an empty value parses to `NaN`, which is not `''`, so the abstention
 * does not fire and the comparison runs and fails. That asymmetry is upstream's
 * and is preserved: the backend behaves the same way, and "fixing" it here would
 * make the view flicker as the server's answer replaced ours.
 */
export function matchesEqual(
  rowValue: unknown,
  filterValue: string,
  field: Field,
): MatchResult {
  const handler = handlerFor(field)
  const rowVal = handler.parseRowValue(rowValue === null ? '' : rowValue, field)
  const filterVal = handler.parseFilterValue(filterValue, field)
  if (filterVal === '') return null
  return rowVal === filterVal
}

export function matchesNotEqual(
  rowValue: unknown,
  filterValue: string,
  field: Field,
): MatchResult {
  const result = matchesEqual(rowValue, filterValue, field)
  return result === null ? null : !result
}

/* --------------------------------------------------------------- ordering */

type Comparison = (a: number, b: number) => boolean

/**
 * Builds an ordering filter.
 *
 * Both sides must be finite. A cell that is empty, or a filter value that is
 * not a number, matches nothing rather than sorting as zero -- an empty cell is
 * not "lower than 5".
 */
export function orderingFilter(compare: Comparison) {
  return (rowValue: unknown, filterValue: string, field: Field): MatchResult => {
    if (filterValue === '') return null
    const handler = handlerFor(field)
    const rowVal = handler.parseRowValue(rowValue, field)
    const filterVal = handler.parseFilterValue(filterValue, field)
    return (
      typeof rowVal === 'number' &&
      typeof filterVal === 'number' &&
      Number.isFinite(rowVal) &&
      Number.isFinite(filterVal) &&
      compare(rowVal, filterVal)
    )
  }
}

/* -------------------------------------------------------------- substring */

/**
 * Builds a substring filter.
 *
 * An empty filter value matches everything -- returned as `true` rather than
 * `null`, which is upstream's shape. The two are treated the same by the row
 * matcher, so the distinction only shows in a direct test.
 */
function substringFilter(
  kindOf: (field: Field) => ContainsKind,
  predicate: (humanReadableValue: string, filterValue: string) => boolean,
  negate: boolean,
) {
  return (
    rowValue: unknown,
    filterValue: string,
    field: Field,
    context?: FilterContext,
  ): MatchResult => {
    if (filterValue === '') return true

    const kind = kindOf(field)
    if (kind === 'none') return negate

    const matched =
      kind === 'filename'
        ? filenameContains(rowValue as FileCellItem[], filterValue)
        : predicate(humanReadable(field, rowValue, context), filterValue)

    return negate ? !matched : matched
  }
}

export const matchesContains = substringFilter(containsKindOf, genericContains, false)
export const matchesContainsNot = substringFilter(containsKindOf, genericContains, true)
export const matchesContainsWord = substringFilter(
  containsWordKindOf,
  genericContainsWord,
  false,
)
export const matchesDoesntContainWord = substringFilter(
  containsWordKindOf,
  genericContainsWord,
  true,
)

/**
 * `filename_contains` reads the attachments, never their joined names.
 *
 * Separate from `contains` because it applies to a file column whose
 * `containsKind` is already `'filename'`; going through the generic path would
 * reach the same predicate, but naming it here keeps the file column's one
 * filter from depending on that coincidence.
 */
export function matchesFilenameContains(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  if (filterValue === '') return true
  return filenameContains(rowValue as FileCellItem[], filterValue)
}

/* -------------------------------------------------------------- emptiness */

export function matchesEmpty(rowValue: unknown, _f: string, field: Field): MatchResult {
  return behaviorOf(field.type).isEmpty(field, rowValue)
}

export function matchesNotEmpty(
  rowValue: unknown,
  _f: string,
  field: Field,
): MatchResult {
  return !behaviorOf(field.type).isEmpty(field, rowValue)
}

/* ------------------------------------------------------------ odds and ends */

/**
 * Whether the cell is shorter than the filter value.
 *
 * Abstains -- as `true` -- when the limit is not a number or is zero, so a
 * half-typed or cleared filter shows every row.
 */
export function matchesLengthIsLowerThan(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  // `Number.isNaN(Number(''))` is false -- the empty string reads as zero -- so
  // a cleared filter falls through to the comparison, where `length < ''`
  // coerces to `length < 0` and matches nothing. Upstream guards `filterValue
  // === 0` against the raw string, which for a string is never true and so
  // never fires. Both are preserved: the abstention deliberately does not cover
  // the cleared filter, because the backend does not either.
  if (Number.isNaN(Number(filterValue)) || rowValue === null) return true
  return stringify(rowValue).length < Number(filterValue)
}

/** Whether the cell is an even whole number. Takes no filter value. */
export function matchesIsEvenAndWhole(rowValue: unknown): MatchResult {
  const parsed = parseFloat(stringify(rowValue))
  return parsed % 2 === 0 && Number.isInteger(parsed)
}

/** Whether a checkbox matches the filter's own truthiness. */
const TRUE_VALUES = ['y', 't', 'o', 'yes', 'true', 'on', '1']

export function matchesBoolean(rowValue: unknown, filterValue: string): MatchResult {
  const wanted = TRUE_VALUES.includes(
    (filterValue === null ? false : filterValue).toString().toLowerCase().trim(),
  )
  const actual =
    rowValue === null
      ? false
      : TRUE_VALUES.includes(stringify(rowValue).toLowerCase().trim())
  return wanted ? actual : !actual
}

/* -------------------------------------------------------------- file types */

/**
 * Whether any attachment is an image, or any is not.
 *
 * A filter value naming neither matches every row: the only two values the input
 * offers are `image` and `document`, so anything else is a filter that has not
 * been filled in.
 */
export function matchesHasFileType(rowValue: unknown, filterValue: string): MatchResult {
  const isImage = filterValue === 'image'
  const isDocument = filterValue === 'document'
  if (!isImage && !isDocument) return true
  if (!Array.isArray(rowValue)) return false
  return (rowValue as { is_image?: boolean }[]).some((file) => file.is_image === isImage)
}

/** Whether the cell holds fewer attachments than the filter value. */
export function matchesFilesLowerThan(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  // Anything with a length counts, not just an array. A file cell is always an
  // array in practice, but upstream reads `.length` off whatever it is handed
  // and an empty string therefore counts as zero files.
  const length = (rowValue as { length?: unknown } | null)?.length
  if (typeof length !== 'number') return false
  return length < parseInt(filterValue, 10)
}
